import { type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext.tsx";

/**
 * Gate a route (or subtree) on the server-session auth state (controlplane-auth task 11.2, echoing
 * AgentDNA's `src/components/ProtectedRoute.tsx`).
 *
 * This is the SPA's CLIENT-SIDE reaction to the server's auth contract, not a security control. The
 * real boundary is the BFF: an unauthenticated `/api/*` request is answered with **401 JSON, never a
 * 3xx redirect** (design §Error Handling). `AuthContext` reads the current session from the server;
 * when it reports no session (a 401 surfaced as `authenticated: false`), THIS component is what the
 * shell does about it — it sends the browser to the login route. The server never redirects; the
 * client does, after it learns there is no session.
 *
 * Three states:
 *   - loading       → render the `fallback` (a neutral placeholder) while the first session read is
 *                     in flight, so we neither flash the protected content nor bounce to login before
 *                     we know the answer.
 *   - unauthenticated → `<Navigate>` to `loginPath`, carrying the attempted location in router state
 *                     so the login page can send the operator back after they sign in.
 *   - authenticated → render the protected `children`.
 *
 * It intentionally does NOT enforce roles — role-gating of individual controls is the screens' job
 * (via the resolved `role`), and the server re-enforces every operation regardless. This component
 * answers only "is there a session?".
 */

export interface ProtectedRouteProps {
  /** The protected content, rendered only when a session is confirmed. */
  readonly children: ReactNode;
  /** Where to send an unauthenticated visitor. Defaults to `/login`. */
  readonly loginPath?: string;
  /** Shown while the first session read is still in flight. Defaults to an accessible placeholder. */
  readonly fallback?: ReactNode;
}

export function ProtectedRoute(props: ProtectedRouteProps): ReactNode {
  const auth = useAuth();
  const location = useLocation();
  const loginPath = props.loginPath ?? "/login";

  if (auth.loading) {
    return (
      props.fallback ?? (
        <div className="oa-auth oa-protected-loading" role="status" aria-live="polite">
          <span className="oa-protected-loading__text">Checking your session…</span>
        </div>
      )
    );
  }

  if (!auth.authenticated) {
    // Carry the attempted path so the login page can return here after a successful sign-in. `replace`
    // keeps the unauthenticated URL out of history (Back should not re-enter the gated route).
    return <Navigate to={loginPath} replace state={{ from: location }} />;
  }

  return <>{props.children}</>;
}
